import React, { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import UnsavedChangesDialog from "@/components/common/UnsavedChangesDialog";

// Fase 9 — guarda global de "alterações por guardar". A app usa
// <BrowserRouter> declarativo (não um data router), pelo que useBlocker/
// unstable_useBlocker (React Router v6/v7) não estão disponíveis sem migrar
// toda a arquitetura de routing (fora do âmbito deste bloco — Fase 10).
// Este contexto substitui esse mecanismo por: (1) registo de dirty por
// página/diálogo via useUnsavedChangesGuard; (2) interceção em fase de
// captura de cliques em <a>/<Link>/<NavLink> internos; (3) beforeunload;
// (4) truque de popstate para o "recuar" do browser (melhor esforço — ver
// limitações documentadas no relatório final).
const UnsavedChangesContext = createContext(null);

function isPlainLeftClick(e) {
  return e.button === 0 && !e.metaKey && !e.altKey && !e.ctrlKey && !e.shiftKey;
}

function internalHrefFromClick(e) {
  const a = e.target instanceof Element ? e.target.closest("a[href]") : null;
  if (!a) return null;
  if (a.target && a.target !== "_self") return null;
  if (a.hasAttribute("download")) return null;
  const href = a.getAttribute("href");
  if (!href || href.startsWith("#")) return null;
  let url;
  try {
    url = new URL(href, window.location.href);
  } catch {
    return null;
  }
  if (url.origin !== window.location.origin) return null;
  return url.pathname + url.search + url.hash;
}

export function UnsavedChangesProvider({ children }) {
  const navigate = useNavigate();
  const dirtyIdsRef = useRef(new Set());
  const [dirty, setDirty] = useState(false);
  const dirtyRef = useRef(false);
  const [modalOpen, setModalOpen] = useState(false);
  const pendingActionRef = useRef(null);
  const historyGuardCountRef = useRef(0);

  const setGuardDirty = useCallback((id, isDirty) => {
    if (isDirty) dirtyIdsRef.current.add(id);
    else dirtyIdsRef.current.delete(id);
    const next = dirtyIdsRef.current.size > 0;
    if (next !== dirtyRef.current) {
      dirtyRef.current = next;
      setDirty(next);
    }
  }, []);

  const requestConfirmation = useCallback((action) => {
    pendingActionRef.current = action;
    setModalOpen(true);
  }, []);

  const confirmIfDirty = useCallback((action) => {
    if (dirtyRef.current) {
      requestConfirmation(action);
    } else {
      action();
    }
  }, [requestConfirmation]);

  const guardedNavigate = useCallback((to, options) => {
    confirmIfDirty(() => (options ? navigate(to, options) : navigate(to)));
  }, [confirmIfDirty, navigate]);

  const confirmLeave = useCallback(() => {
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    setModalOpen(false);
    if (action) action();
  }, []);

  const cancelLeave = useCallback(() => {
    pendingActionRef.current = null;
    setModalOpen(false);
  }, []);

  // Fase 4 — beforeunload só ativo enquanto existir alguma alteração por
  // guardar; removido no cleanup do efeito (dirty=false, unmount, etc.).
  useEffect(() => {
    if (!dirty) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  // Recuar do browser — melhor esforço via entrada extra no histórico.
  useEffect(() => {
    if (!dirty) {
      historyGuardCountRef.current = 0;
      return undefined;
    }
    window.history.pushState({ __unsavedGuard: true }, "", window.location.href);
    historyGuardCountRef.current += 1;
    const onPopState = () => {
      // Re-arma a entrada-armadilha imediatamente (mesmo URL) para o
      // <Routes> não desmontar a página antes de o utilizador decidir.
      window.history.pushState({ __unsavedGuard: true }, "", window.location.href);
      historyGuardCountRef.current += 1;
      requestConfirmation(() => {
        const n = historyGuardCountRef.current;
        historyGuardCountRef.current = 0;
        window.history.go(-n);
      });
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [dirty, requestConfirmation]);

  // Navegação interna via <a>/<Link>/<NavLink> (menu lateral, bottom nav) —
  // interceção em fase de captura, inativa quando não há alterações por
  // guardar (zero impacto no comportamento normal do React Router).
  useEffect(() => {
    const handler = (e) => {
      if (!dirtyRef.current) return;
      if (e.defaultPrevented || !isPlainLeftClick(e)) return;
      const dest = internalHrefFromClick(e);
      if (!dest) return;
      e.preventDefault();
      e.stopPropagation();
      requestConfirmation(() => navigate(dest));
    };
    document.addEventListener("click", handler, true);
    return () => document.removeEventListener("click", handler, true);
  }, [navigate, requestConfirmation]);

  const value = useMemo(
    () => ({ setGuardDirty, guardedNavigate, confirmIfDirty, dirty }),
    [setGuardDirty, guardedNavigate, confirmIfDirty, dirty],
  );

  return (
    <UnsavedChangesContext.Provider value={value}>
      {children}
      <UnsavedChangesDialog
        open={modalOpen}
        onOpenChange={(open) => { if (!open) cancelLeave(); }}
        onConfirm={confirmLeave}
        onCancel={cancelLeave}
      />
    </UnsavedChangesContext.Provider>
  );
}

const NOOP_GUARD = { guardedNavigate: () => {}, confirmIfDirty: (action) => action && action() };

// Fase 9 — hook reutilizável: páginas/diálogos registam o seu próprio
// booleano `isDirty` (derivado localmente, ex. diff de snapshot); o
// contexto agrega tudo (OR) numa única guarda global. `guardedNavigate` e
// `confirmIfDirty` servem para os botões internos (Voltar, Cancelar, FAB,
// logo, breadcrumb) respeitarem a mesma confirmação que a navegação externa.
export function useUnsavedChangesGuard(isDirty) {
  const ctx = useContext(UnsavedChangesContext);
  const id = useId();
  useEffect(() => {
    if (!ctx) return undefined;
    ctx.setGuardDirty(id, isDirty);
    return () => ctx.setGuardDirty(id, false);
  }, [ctx, id, isDirty]);
  return ctx ? { guardedNavigate: ctx.guardedNavigate, confirmIfDirty: ctx.confirmIfDirty } : NOOP_GUARD;
}

// Variante sem registo de dirty — para pontos de navegação (logo, FAB, menu
// de utilizador) que só precisam de respeitar a guarda global, sem serem
// eles próprios uma fonte de alterações por guardar.
export function useGuardedNavigate() {
  const ctx = useContext(UnsavedChangesContext);
  return ctx ? ctx.guardedNavigate : NOOP_GUARD.guardedNavigate;
}

// Idem, para ações que não são uma navegação de rota directa (ex.: terminar
// sessão) mas que também devem respeitar a guarda global antes de prosseguir.
export function useConfirmIfDirty() {
  const ctx = useContext(UnsavedChangesContext);
  return ctx ? ctx.confirmIfDirty : NOOP_GUARD.confirmIfDirty;
}
